/**
 * SUP-2 (K37) — client-side PII/secret redaction for bug-report context_json.
 *
 * The bug-report `context_json` snapshots the route (incl. query string),
 * recent client errors, and breadcrumbs — any of which can embed customer
 * PII (email/phone/VIN) OR secrets/tokens (a `?token=`/`?authorization=`
 * query param, a `Bearer ...` header fragment in an error string, an
 * `sk_`/`whsec_` key, a JWT). This redacts those patterns BEFORE the report
 * is persisted, so neither raw customer PII nor credentials are stored on
 * `bug_reports` (which support staff can read).
 *
 * Mirrors the edge `aiPayloadScrubber` patterns (Deno-only, not importable
 * from the client bundle). Pure + unit-tested. Prefers over-redaction.
 */

// The DEFINITION of what is masked as an email. Never run with String.replace:
// from every start inside a long run of local-part characters it rescans to the
// run's end (O(n²) — 20–40 s in a browser tab on one 128 KiB token).
// replaceEmails() below applies exactly this pattern in linear time.
export const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const VIN_RE = /\b[A-HJ-NPR-Z0-9]{17}\b/g;
const PHONE_RE = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

// Secret / credential shapes.
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._\-]{8,}/gi;
const JWT_RE = /\beyJ[A-Za-z0-9._-]{20,}/g; // JWT header prefix
const SECRET_PREFIX_RE = /\b(?:sk|rk|pk|whsec|re)_[A-Za-z0-9_]{8,}/g;
// `?token=...` / `&authorization=...` / `secret=...` etc. — redact the VALUE
// of any query/kv pair whose key contains a sensitive word.
//
// (?=[\w.\-]*=) — a necessary condition for any match (the key is the whole
// [\w.-] run up to an '='), checked once per start BEFORE the keyword search
// can backtrack. Same matches; linear instead of O(n²) on a long key-like run.
const SENSITIVE_KV_RE =
  /([?&;]\s*)(?=[\w.\-]*=)([\w.\-]*(?:token|secret|password|passwd|pwd|authorization|apikey|api[_-]?key|access[_-]?token|refresh[_-]?token|session|sid|signature|bearer|jwt|otp|\bkey\b|\bauth\b|\bcode\b)[\w.\-]*=)([^&;#\s"']+)/gi;

// Local-part characters of EMAIL_RE: [a-zA-Z0-9._%+-]
const isEmailLocalChar = (c: number): boolean =>
  (c >= 97 && c <= 122) || (c >= 65 && c <= 90) || (c >= 48 && c <= 57) ||
  c === 46 /* . */ || c === 95 /* _ */ || c === 37 /* % */ || c === 43 /* + */ || c === 45 /* - */;
const EMAIL_DOMAIN_TAIL = /[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/y;

/**
 * text.replace(EMAIL_RE, replacer), in LINEAR time — the same matches in the
 * same order. A deliberate MIRROR of replaceEmails() in
 * supabase/functions/_shared/aiPayloadScrubber.ts (that module imports Edge-only
 * code and cannot be bundled into the browser). Walk the '@'s left to right;
 * a match starts at the beginning of the local-character run before the '@'
 * (never before the previous match's end) and needs the domain tail after it.
 * tests/unit/utils/scrubBugContextLinear.test.ts pins this against both the
 * regex and the server implementation.
 */
export function replaceEmails(text: string, replacer: (match: string) => string): string {
  let out = '';
  let last = 0;
  let at = text.indexOf('@');
  while (at !== -1) {
    let start = at;
    while (start > last && isEmailLocalChar(text.charCodeAt(start - 1))) start--;
    if (start < at) {
      EMAIL_DOMAIN_TAIL.lastIndex = at + 1;
      if (EMAIL_DOMAIN_TAIL.exec(text)) {
        const end = EMAIL_DOMAIN_TAIL.lastIndex;
        out += text.slice(last, start) + replacer(text.slice(start, end));
        last = end;
        at = text.indexOf('@', last);
        continue;
      }
    }
    at = text.indexOf('@', at + 1);
  }
  return last === 0 ? text : out + text.slice(last);
}

/** Redact PII + secrets/tokens from a single string. */
export function redactPII(input: string): string {
  if (!input) return input;
  const secretsRedacted = input
    // secrets first — so their digit/char runs aren't partially matched below
    .replace(SENSITIVE_KV_RE, '$1$2[redacted]')
    .replace(BEARER_RE, 'Bearer [redacted]')
    .replace(JWT_RE, '[redacted-jwt]')
    .replace(SECRET_PREFIX_RE, '[redacted-secret]');
  // then PII (emails through the linear EMAIL_RE)
  return replaceEmails(secretsRedacted, () => '[redacted-email]')
    .replace(VIN_RE, '[redacted-vin]')
    .replace(PHONE_RE, '[redacted-phone]');
}

/** Recursively redact PII/secrets in all string values of a JSON-ish value. */
export function scrubDeep<T>(value: T): T {
  if (typeof value === 'string') return redactPII(value) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrubDeep(v);
    }
    return out as unknown as T;
  }
  return value;
}
